import { Subflow } from '../models/Subflow';
import { FlowConfigurationViewModel } from '../models/viewModels/FlowConfigurationViewModel';
const LIME_SUBFLOW_TYPE = 'application/vnd.iris.builder.subflows.subflow+json';
const BUILDER_POSTMASTER = 'postmaster@builder.msging.net';
const LIME_DOCUMENT_TYPE = 'application/vnd.lime.document+json';
export const SUBFLOW_URL = '/subflows';
export const SUBFLOW_KEY = 'blip_portal:builder_working_subflow';
export const SUBFLOW_ID_KEY = 'blip_portal:builder_working_subflow_id';
export var SubflowModalAction;
(function (SubflowModalAction) {
    SubflowModalAction["Create"] = "create";
    SubflowModalAction["Update"] = "update";
})(SubflowModalAction || (SubflowModalAction = {}));
export var SubflowFlowStatus;
(function (SubflowFlowStatus) {
    SubflowFlowStatus["EditedFlow"] = "edited-flow";
    SubflowFlowStatus["EditedFlowConfiguration"] = "edited-configurations";
    SubflowFlowStatus["PublishedFlow"] = "flow-published";
})(SubflowFlowStatus || (SubflowFlowStatus = {}));
export class SubflowService {
    static $inject = ['MessagingHubService'];
    constructor(MessagingHubService) {
        this.MessagingHubService = MessagingHubService;
    }
    async createSubflow(subflow) {
        return this.set(subflow);
    }
    async updateSubflow(shortName, subflow) {
        return this.set({ shortName, ...subflow });
    }
    async set(resource) {
        const newSubflow = new Subflow();
        const requestURL = resource.shortName && resource.shortName !== '' ? `${SUBFLOW_URL}/${resource.shortName}` : `${SUBFLOW_URL}`;
        if (resource.tags) {
            resource.tags = ',' + resource.tags + ',';
        }
        const newResource = await this.MessagingHubService.sendCommand({
            to: BUILDER_POSTMASTER,
            method: 'set',
            type: LIME_SUBFLOW_TYPE,
            uri: requestURL,
            resource,
        });
        if (newResource) {
            newSubflow.configuration = newResource;
            newSubflow.shortName = newResource?.shortName;
        }
        return newResource && newSubflow?.configuration ? newSubflow : undefined;
    }
    async registerFlowStatus(shortName, document, name, key) {
        const jsonDocument = JSON.stringify(document);
        return await this.MessagingHubService.sendCommand({
            to: BUILDER_POSTMASTER,
            method: 'set',
            type: LIME_DOCUMENT_TYPE,
            uri: `${SUBFLOW_URL}/${shortName}/publish`,
            resource: {
                name,
                document: jsonDocument,
                key: key ?? '',
            },
        });
    }
    async publish(shortName, flow, key) {
        return await this.registerFlowStatus(shortName, flow, SubflowFlowStatus.PublishedFlow, key);
    }
    async setFlow(shortName, flow) {
        return await this.registerFlowStatus(shortName, flow, SubflowFlowStatus.EditedFlow);
    }
    async setFlowConfiguration(shortName, configuration) {
        return await this.registerFlowStatus(shortName, configuration, SubflowFlowStatus.EditedFlowConfiguration);
    }
    async get(shortName) {
        const resource = await this.MessagingHubService.sendCommand({
            to: BUILDER_POSTMASTER,
            method: 'get',
            uri: `${SUBFLOW_URL}/${shortName}`,
        });
        const newSubflow = new Subflow();
        newSubflow.shortName = resource ? resource.shortName : '';
        newSubflow.configuration = resource;
        return newSubflow;
    }
    async changeActive(shortName, value) {
        await this.MessagingHubService.sendCommand({
            to: BUILDER_POSTMASTER,
            type: LIME_DOCUMENT_TYPE,
            method: 'set',
            uri: `${SUBFLOW_URL}/${shortName}/active`,
            resource: { Value: value },
        });
    }
    async getStatus(shortName, name) {
        return await this.MessagingHubService.sendCommand({
            method: 'get',
            to: BUILDER_POSTMASTER,
            uri: `${SUBFLOW_URL}/${shortName}/configurations/${name}`,
        });
    }
    async getSubflowsByShortNames(shortNames) {
        const uri = `${SUBFLOW_URL}`;
        const formattedShortNames = shortNames.map(encodeURIComponent).join(';');
        const result = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: BUILDER_POSTMASTER,
            uri: `${uri}?shortNames=${formattedShortNames}`,
        });
        return result.items;
    }
    async getSubflowsConfigurationsById(subflowsIds) {
        const uri = `${SUBFLOW_URL}/configurations/by-ids`;
        const formattedSubflowIds = subflowsIds.map(encodeURIComponent).join(';');
        const result = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: BUILDER_POSTMASTER,
            uri: `${uri}?Ids=${formattedSubflowIds}`,
        });
        return result.items;
    }
    async getEditedSubflowsOfFlow(flow) {
        const subflowsShortName = this.getSubflowsShortNameFromFlow(flow);
        if (!subflowsShortName?.length) {
            return;
        }
        const exportedSubflows = [];
        for (const subflowShortName of subflowsShortName) {
            const flow = await this.getEditedFlowOfSubflow(subflowShortName, undefined);
            const configuration = await this.getEditedSubflowConfiguration(subflowShortName);
            exportedSubflows.push({
                shortName: subflowShortName,
                flow,
                configuration,
            });
        }
        return exportedSubflows;
    }
    getSubflowsShortNameFromFlow(flow) {
        return Object.entries(flow)
            .map(([key, value]) => key.includes('subflow:') ? value.shortNameOfSubflow : undefined)
            .filter(item => item);
    }
    async getEditedFlowOfSubflow(shortName, flowDefault) {
        try {
            const states = await this.getStatus(shortName, SubflowFlowStatus.EditedFlow);
            if (!states || !states.document) {
                return;
            }
            return JSON.parse(states?.document);
        }
        catch (e) {
            // If is bucket empty, create the view model using the published model.
            if (e.code == 67) {
                return flowDefault;
            }
            else {
                throw e;
            }
        }
    }
    async getPublishedFlowOfSubflow(shortName) {
        const states = await this.getStatus(shortName, SubflowFlowStatus.PublishedFlow);
        if (!states || !states.document) {
            return;
        }
        const document = JSON.parse(states?.document);
        return document.settings.flow;
    }
    async getEditedSubflowConfiguration(shortName) {
        try {
            const configuration = await this.getStatus(shortName, SubflowFlowStatus.EditedFlowConfiguration);
            return configuration && configuration.document
                ? JSON.parse(configuration.document)
                : undefined;
        }
        catch (e) {
            // If is bucket empty, create a default configuration
            if (e.code == 67) {
                return FlowConfigurationViewModel.createDefault();
            }
            else {
                console.error(e);
                throw e;
            }
        }
    }
    async createNewSubflow(subflowConfiguration, flow, flowConfiguration = FlowConfigurationViewModel.createDefault()) {
        const newSubflow = await this.createSubflow(subflowConfiguration);
        if (flow) {
            await this.setFlow(newSubflow.shortName, flow);
        }
        await this.setFlowConfiguration(newSubflow.shortName, flowConfiguration);
        return newSubflow;
    }
    async getSubflowConfigurationOrDefault(shortName, flow) {
        let config;
        try {
            const { configuration } = await this.get(shortName);
            config = configuration;
        }
        catch (e) {
            let subflowInJson;
            for (const key in flow) {
                if (flow[key]?.shortNameOfSubflow === shortName) {
                    subflowInJson = flow[key];
                    break;
                }
            }
            if (!subflowInJson) {
                return;
            }
            config = {
                name: subflowInJson.$title,
                image: subflowInJson.$imageUri,
                description: '',
                tags: '',
                tenantId: '',
                isShared: false,
                userIdentity: '',
            };
        }
        finally {
            return config;
        }
    }
}
SubflowService.$inject = ['MessagingHubService'];
